import React from 'react';
import type { QuoteFormData, QuoteFormErrors } from '../../types/quote';
import { FiCopy } from 'react-icons/fi';
import { CanadaAddressAutocomplete } from './CanadaAddressAutocomplete';
import { MuiTextInput } from './MuiInputs';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import PhoneIcon from '@mui/icons-material/Phone';

interface StepPickupDetailsProps {
  data: QuoteFormData;
  errors: QuoteFormErrors;
  onChange: (field: keyof QuoteFormData, value: any) => void;
  onCopyFromCustomer?: () => void;
}

export const StepPickupDetails: React.FC<StepPickupDetailsProps> = ({
  data,
  errors,
  onChange,
  onCopyFromCustomer,
}) => {
  return (
    <div className="space-y-6">
      <div className="border-b border-gray-100 pb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div>
          <span className="text-xs uppercase tracking-widest text-[#F55109] font-bold">
            Step 2 of 6
          </span>
          <h3 className="text-2xl sm:text-3xl font-bold text-[#2C2C2C] special-header mt-1">
            SECTION 2 — PICKUP DETAILS
          </h3>
          <p className="text-sm text-gray-500 mt-1">
            Provide the location and on-site contact person where items will be picked up.
          </p>
        </div>

        {onCopyFromCustomer && (
          <button
            type="button"
            onClick={onCopyFromCustomer}
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#F55109] hover:text-[#d44304] bg-[#F55109]/10 hover:bg-[#F55109]/20 px-3 py-1.5 rounded-lg transition-colors self-start sm:self-center cursor-pointer"
          >
            <FiCopy className="w-3.5 h-3.5" />
            Same as customer contact
          </button>
        )}
      </div>

      <div className="space-y-6">
        {/* Pickup Address with Canada Google Autocomplete (MUI) */}
        <CanadaAddressAutocomplete
          id="pickupAddress"
          label="Pickup Address (Long Answer)"
          required
          rows={3}
          value={data.pickupAddress}
          onChange={(address, details) => {
            onChange('pickupAddress', address);
            if (details) {
              onChange('pickupDetails', details);
            }
          }}
          error={errors.pickupAddress}
          placeholder="Start typing Canadian address (e.g. 100 King St W, Toronto, ON)..."
          helperText="Restricted to Canada. Select suggestion or add unit/suite/door details."
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-2">
          {/* Pickup Contact Name */}
          <MuiTextInput
            id="pickupContactName"
            label="Pickup Contact Name"
            value={data.pickupContactName}
            onChange={(val) => onChange('pickupContactName', val)}
            placeholder="Name of person handing over package"
            required
            errorMessage={errors.pickupContactName}
            startIcon={<PersonOutlineIcon fontSize="small" />}
          />

          {/* Pickup Contact Phone */}
          <MuiTextInput
            id="pickupContactPhone"
            label="Pickup Contact Phone"
            type="tel"
            value={data.pickupContactPhone}
            onChange={(val) => onChange('pickupContactPhone', val)}
            placeholder="Phone number of pickup contact"
            required
            errorMessage={errors.pickupContactPhone}
            startIcon={<PhoneIcon fontSize="small" />}
          />
        </div>
      </div>
    </div>
  );
};
